import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Download, 
  RotateCcw, 
  ArrowLeft, 
  Upload, 
  FileAudio, 
  Check, 
  Loader2 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/db/supabase';

const TranscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [transcription, setTranscription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    if (uploadedFile) {
      if (uploadedFile.size > 100 * 1024 * 1024) {
        toast.error('文件大小不能超过 100MB');
        return;
      }
      setFile(uploadedFile);
      toast.success('文件上传成功');
    }
  };

  const handleTranscribe = async () => {
    if (!file) {
      toast.error('请先上传音视频文件');
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    // Mock processing step 1
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 60) {
          clearInterval(interval);
          return 60;
        }
        return prev + 5;
      });
    }, 200);

    try {
      // Step 2: Real AI refinement/summarization or transcript generation
      const { data, error } = await supabase.functions.invoke('ai_text_generation', {
        body: {
          messages: [
            { role: 'user', content: `请模拟一个针对“${file.name}”的视频字幕转写，包含时间戳，格式如下：[00:00:00] 内容...` }
          ],
          task_type: 'transcription'
        }
      });

      if (error) throw new Error(error.message);

      setProgress(100);
      setTranscription(data.content || '生成失败，请重试');
      toast.success('转写完成');
    } catch (err: any) {
      toast.error(`转写失败: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([transcription], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'transcription.txt';
    a.click();
    toast.success('已下载字幕文件');
  };

  return (
    <div className="min-h-screen bg-background pb-20 animate-fade-in">
      <div className="tech-flow-bg h-[160px] flex items-center px-6">
        <div className="container mx-auto">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/10 mb-2 gap-2"
            onClick={() => navigate('/ai-hub')}
          >
            <ArrowLeft className="h-4 w-4" />
            返回 AI 助手
          </Button>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <FileAudio className="h-6 w-6" />
            实时字幕转写
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-xl glass-card border-border/50 shadow-lg">
            <CardHeader className="p-4 border-b border-border/50">
              <CardTitle className="text-lg flex items-center gap-2 text-primary font-bold">
                上传音视频
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="border-2 border-dashed border-primary/30 rounded-xl p-10 text-center bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer relative">
                <input 
                  type="file" 
                  accept="audio/*,video/*" 
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleFileUpload}
                />
                <Upload className="h-12 w-12 text-primary mx-auto mb-4" />
                <h4 className="text-sm font-bold text-foreground mb-1">点击或拖拽上传</h4>
                <p className="text-xs text-muted-foreground">支持 MP3、MP4、WAV、M4A 等格式，最大 100MB</p>
              </div>

              {file && (
                <div className="p-4 rounded-lg bg-muted/50 border border-border flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileAudio className="h-8 w-8 text-primary" />
                    <div>
                      <h5 className="text-sm font-bold">{file.name}</h5>
                      <p className="text-xs text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                  <Badge className="bg-success text-white">
                    <Check className="h-3 w-3 mr-1" /> 已上传
                  </Badge>
                </div>
              )}

              {isProcessing && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-muted-foreground">转写进度</span>
                    <span className="font-bold text-primary">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}
            </CardContent>
            <CardFooter className="p-4 pt-2">
              <Button 
                className="w-full bg-primary text-white hover:bg-primary/90 h-12 shadow-lg btn-ripple flex items-center gap-2 font-bold"
                onClick={handleTranscribe}
                disabled={isProcessing || !file}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    正在转写中...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    开始转写
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>

          <Card className="rounded-xl glass-card border-border/50 shadow-lg bg-gradient-to-br from-primary/5 to-secondary/5">
            <CardHeader className="p-4 border-b border-border/50 flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-primary font-bold">
                转写结果
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 gap-1 text-primary hover:bg-primary/10"
                onClick={handleDownload}
                disabled={!transcription}
              >
                <Download className="h-3 w-3" /> 下载
              </Button>
            </CardHeader>
            <CardContent className="p-4 pt-6">
              <Textarea 
                className="min-h-[400px] rounded-lg border-primary/10 bg-white/50 resize-none text-sm leading-relaxed p-4 font-mono"
                value={transcription}
                onChange={(e) => setTranscription(e.target.value)}
                placeholder="转写结果将在此显示，支持手动编辑..."
              />
            </CardContent>
            <CardFooter className="p-4 pt-2">
              <div className="w-full grid grid-cols-2 gap-3">
                <Button 
                  variant="outline" 
                  className="gap-1.5 h-10 border-primary/20 text-primary hover:bg-primary/5 font-bold" 
                  disabled={!transcription}
                  onClick={() => {}}
                >
                  <RotateCcw className="h-3.5 w-3.5" /> 重新转写
                </Button>
                <Button 
                  className="gap-1.5 h-10 bg-secondary text-white hover:bg-secondary/90 shadow-md font-bold" 
                  disabled={!transcription}
                  onClick={() => {}}
                >
                  <Download className="h-3.5 w-3.5" /> 导出 SRT
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default TranscriptionPage;
